/**
 * Las clases que comparten TODAS las tarjetas de la base de conocimiento: las
 * fuentes cargadas, las sugerencias y el material (archivos, páginas, textos).
 *
 * Están en un módulo propio y no en el componente que las estrenó porque ya las
 * usan tres archivos, y la única forma de que tres grillas sigan viéndose como
 * una sola dentro de seis meses es que salgan del mismo string. Si cambia una,
 * cambian las tres.
 *
 * Ver ADR-2026-08-17-estudio-marketing-fuentes-tarjetas-media-altura y
 * ADR-2026-08-17-estudio-marketing-modal-anadir-fuente.
 */

/**
 * MEDIA ALTURA, en concreto: `108px`.
 *
 * Una tarjeta de conexión mide ~232px con su contenido más alto (borde 2 +
 * `p-5` 40 + marco del logo 44 + `mt-4` + píldora de estado ~28 + `mt-2` + dos
 * líneas de detalle ~41 + `mt-4`/`pt-1` + botón 35). Dos de estas tarjetas más
 * el `gap-4` (16px) de la MISMA grilla dan 108·2 + 16 = 232: dos fuentes ocupan
 * exactamente el hueco de una conexión, que es lo que "la mitad del alto"
 * quiere decir cuando las dos grillas están una debajo de la otra.
 *
 * Va como `min-h-` y no como `h-`: los ítems de la grilla se estiran solos al
 * alto de la fila, así que el mínimo FIJA la fila en 108 mientras el contenido
 * entre (siempre: una sola fila, con `truncate`), pero deja crecer si alguien
 * agranda la tipografía del navegador en vez de recortar el texto.
 */
/**
 * La forma de la tarjeta SIN alto: radio, padding horizontal, alineación y gap.
 * Va separada del mínimo porque no todos los usos lo quieren, y apilar un
 * `min-h-0` encima de un `min-h-[108px]` NO lo pisa: entre dos utilidades de
 * Tailwind que compiten gana la que la hoja genera después, no la que se
 * escribe última en el `className`. Quien no quiera el mínimo, usa esta.
 */
export const CLASE_TARJETA_BASE =
  "flex w-full items-center gap-3 rounded-2xl px-5 text-left";

/**
 * La tarjeta de un tercio de ancho, con su alto mínimo de 108. Ese número está
 * calculado PARA ESE ANCHO, donde el texto ocupa dos líneas: en una fila que va
 * a todo el ancho de la pantalla el contenido entra en un renglón y 108 se ve
 * enorme (ver `PasoConexiones`, que usa la base y pone su propio padding).
 */
export const CLASE_TARJETA = `${CLASE_TARJETA_BASE} min-h-[108px]`;

/** La grilla es la de las conexiones, letra por letra: mismo ancho de columna y
 *  mismo gap, que es de donde sale que las tarjetas se vean de la misma
 *  familia. Si cambia una, cambian las dos. */
export const CLASE_GRILLA = "mt-2 grid gap-4 lg:grid-cols-3";

/** Mismo anillo de foco que las tarjetas de conexión (`SeccionConexiones`). */
export const CLASE_FOCO =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary";

/** El rótulo de cada sub-lista ("Fuentes cargadas", "Material cargado"). */
export const CLASE_ROTULO =
  "text-[12px] font-semibold uppercase tracking-wide text-[rgb(var(--brand-black-rgb)_/_0.45)]";
